/**
 * Setup-flow analytics helpers (H35 T2): pure functions that turn what a setup step already
 * knows into PostHog event properties. They never change what the flow does or shows; every
 * capture goes through `phCapture` (lazy queue, no await). Never pass a password or card value.
 */
import type { PricingPlan } from "@/components/outfame/pricing/plans-data"
import { phCapture, phOnce } from "@/lib/analytics/posthog-client"
import type { AIAnalysis, ProfileData } from "./types"

export type LookupMode = "normal" | "recovery" | "express"
/**
 * Why a profile lookup failed, read from the `/api/<platform>/get-profile` answer (see its header comment):
 * 200 + `false` and 404 are the route's not-found answers; 503 is a provider outage or deadline; 429 the rate limit;
 * other 5xx a server fault; any other unreadable answer is bad_response. Thrown before a response arrives is
 * network_error, a body that is not JSON is invalid_json, anything later in the overlay is client_error.
 */
export type LookupFailure = "not_found" | "provider_unavailable" | "rate_limited" | "server_error" | "bad_response" | "network_error" | "invalid_json" | "client_error" | "unknown_error"
export type LookupResult = "found" | "private" | LookupFailure

/** Failure type of a get-profile answer that did not yield a profile (status + parsed JSON body). */
export function lookupFailureType(status: number, body: unknown): LookupFailure {
    if (status === 404 || (status === 200 && body === false)) return "not_found"
    if (status === 503) return "provider_unavailable"
    if (status === 429) return "rate_limited"
    if (status >= 500) return "server_error"
    return "bad_response"
}

/** Milliseconds since `start` (a performance.now() reading), or null when there is no start. */
export function msSince(start: number | null | undefined): number | null {
    if (typeof start !== "number" || typeof performance === "undefined") return null
    return Math.max(0, Math.round(performance.now() - start))
}

/**
 * Outcome of the "We found your account" lookup as the overlay hands it back. A failed lookup's result is the
 * `errorType` the overlay read from the response (never inferred from the error text); a failure without one is
 * unknown_error.
 */
export function lookupOutcome(result: { profile: ProfileData | null; analysis: AIAnalysis | null; error: string | null; errorType?: LookupFailure }): { result: LookupResult; followers: number | null; niche: string | null; est_growth_shown: string | null; analysis_fallback: boolean | null; error_message: string | null } {
    if (result.error || !result.profile) {
        return { result: result.errorType ?? "unknown_error", followers: null, niche: null, est_growth_shown: null, analysis_fallback: null, error_message: result.error ? result.error.slice(0, 200) : null }
    }
    const a = result.analysis
    return {
        result: result.profile.isPrivate ? "private" : "found",
        followers: Number.isFinite(result.profile.followerCount) ? result.profile.followerCount : null,
        niche: a?.niche ? a.niche.slice(0, 100) : null,
        est_growth_shown: a?.growthPotential?.estimatedFollowersPerMonth ? String(a.growthPotential.estimatedFollowersPerMonth).slice(0, 40) : null,
        // The overlay substitutes a generic analysis when the AI call fails: no suggestions at all.
        analysis_fallback: !a || ((a.suggestedCompetitors?.length || 0) === 0 && (a.suggestedHashtags?.length || 0) === 0),
        error_message: null,
    }
}

type Capture = (event: string, props?: Record<string, unknown>) => void
type Once = (key: string) => boolean

/**
 * PostHog captures for a finished overlay lookup: profile_lookup, plus handle_error when no profile came back. Never
 * express_flow: a failed express lookup leaves the visitor in express mode on the username step, and the retry's
 * sign-up must still record its one express_flow outcome (both events here already carry mode "express").
 */
export function captureLookupOutcome(outcome: ReturnType<typeof lookupOutcome>, ctx: { platform: string; mode: LookupMode; ms: number | null }, capture: Capture = phCapture): void {
    capture("profile_lookup", { platform: ctx.platform, mode: ctx.mode, ms: ctx.ms, ...outcome })
    if (outcome.result !== "found" && outcome.result !== "private") {
        capture("handle_error", { reason: outcome.result, platform: ctx.platform, source: "lookup", mode: ctx.mode })
    }
}

/** express_flow, once per session: whether the express link created the account (used) or why it fell back. */
export function captureExpressFlow(used: boolean, fallbackReason: string | null, capture: Capture = phCapture, once: Once = phOnce): void {
    if (once("express_flow")) capture("express_flow", { used, fallback_reason: fallbackReason })
}

/** Account-step error type from the Supabase sign-up error message (the same strings AccountStep branches on). */
export function signupErrorType(message: string): "exists" | "password_too_long" | "other" {
    if (message === "User already registered") return "exists"
    if (message.includes("exceeds maximum length")) return "password_too_long"
    return "other"
}

/**
 * The prices each plan card shows for `period`, computed exactly as PricingCard renders them
 * (`src/components/outfame/pricing/PricingCard.tsx`, setup passes no discountPercent and keeps
 * the struck list price). Display values in USD; nothing here is used to charge.
 */
export function cardPricesShown(plans: PricingPlan[], period: "Monthly" | "Yearly", yearlyTotalOverrides: Partial<Record<string, number>> = {}) {
    return plans.map((plan) => {
        const basePrice = period === "Monthly" ? plan.firstMonthMonthlyPrice || plan.price.Monthly : plan.price.Yearly
        const override = period === "Yearly" ? yearlyTotalOverrides[plan.name] : undefined
        const yearlyOverride = typeof override === "number" && override > 0 ? override : null
        const price = yearlyOverride !== null ? Math.round((yearlyOverride / 12) * 100) / 100 : parseInt(basePrice)
        const strike = parseInt(plan.monthlyRetailPrice || plan.price.Monthly)
        const monthlyCompare = parseInt(plan.monthlyRetailPrice || plan.firstMonthMonthlyPrice || plan.price.Monthly)
        const yearlySavings = monthlyCompare * 12 - (yearlyOverride ?? parseInt(plan.price.Yearly) * 12)
        return {
            plan: plan.name,
            price,
            list: strike > price ? strike : null,
            per_day: Math.floor((price / 30) * 10) / 10,
            billed_total: period === "Yearly" ? (yearlyOverride ?? (plan.price.YearlyTotal ? Number(plan.price.YearlyTotal) : null)) : price,
            save_label: period === "Yearly" ? `$${yearlySavings.toLocaleString("en-US")}` : null,
            featured: !!plan.featured,
        }
    })
}

/**
 * Login state at the first finished auth check (null until then). Once set it never changes, so the session an
 * express sign-up creates (Supabase fires onAuthStateChange before signUp() resolves) is never read as a visitor
 * who arrived logged in.
 */
export function firstAuthState(latched: boolean | null, authChecked: boolean, isLoggedIn: boolean): boolean | null {
    if (latched !== null || !authChecked) return latched
    return isLoggedIn
}

export type PricesLayout = "full" | "simplified" | "simplified_compare"

/**
 * The plan-card sets the plan screen shows right now, for prices_shown. Empty until the simplified-layout flag has
 * answered (success, non-OK answer or network error), so a visitor is never recorded in the full layout and then in
 * the simplified one after a refresh at the plan step or an OAuth return. Empty too while the simplified flag is on but
 * the arm's latch effect has not switched the layout yet (the first commit). The simplified arm shows the featured
 * card (all cards when none is featured); its "Compare all plans" section adds every card once opened.
 */
export function pricesShownSets(s: { planFlagResolved: boolean; planScreenSimplified: boolean; simplifiedArm: boolean; planInteracted: boolean; compareAllOpen: boolean; featuredPlan: PricingPlan | null; plans: PricingPlan[] }): { layout: PricesLayout; plans: PricingPlan[] }[] {
    if (!s.planFlagResolved) return []
    if (s.planScreenSimplified && !s.simplifiedArm && !s.planInteracted) return []
    if (!s.simplifiedArm) return [{ layout: "full", plans: s.plans }]
    const sets: { layout: PricesLayout; plans: PricingPlan[] }[] = [{ layout: "simplified", plans: s.featuredPlan ? [s.featuredPlan] : s.plans }]
    if (s.compareAllOpen) sets.push({ layout: "simplified_compare", plans: s.plans })
    return sets
}
